/** The pre-check-in settings as an editor edits them — the tour's own settings (plan §2 S5)
 *  and, through the same paths, an operator's: pure transforms over the
 *  `pre_check_settings.<k>` paths, so one action is one undo step over every path it moves.
 *
 *  - A scope (the lead booker, the party members) has a field list, and tag-category
 *    questions: a switch (`<scope>_tag_categories_enabled`) and the categories asked
 *    (`<scope>_selected_tag_categories`, category CODES). The questions are entries of the
 *    field list too (`type: "tag_category"`, `category_id` = the code) — the funnel creates
 *    the fields from the list alone — so editing either rebuilds the list: the regular
 *    fields in their order, then one question per selected category while the switch is on
 *    (the dashboard's shape: `id: tag_category_<code>`). An existing question keeps its
 *    other keys (`multiselect`, `schema`).
 *  - "Same fields for party members" (`apply_same_fields_to_party_members`): while on, the
 *    party's list, switch and categories are the lead booker's — copied when it is turned
 *    on and whenever the lead booker's change (the dashboard copies them on save).
 *  - The lead booker's first name and email are mandatory (always asked, required).
 *
 *  A list stored with questions but the switch off (the old console tab added questions
 *  straight into the list) reads as switched on with those categories, so the first edit
 *  keeps them.
 *
 *  Shared by the tour editor (`features/tours/record`, re-exported from its `precheck.ts`)
 *  and the operator's Pre-check-in tab. */

/** A pre-check setting's draft path. */
export const PRECHECK = (k: string) => `pre_check_settings.${k}`;

/** The keys the editors edit, with the defaults the backend reads a missing key as
 *  (`get_pre_check_settings_from_models`). The rest of a stored object round-trips. */
export const PRECHECK_DEFAULTS: Readonly<Record<string, unknown>> = {
  enabled: false,
  required: false,
  allow_edit_after_checkin: false,
  intro_text: '',
  confirmation_message: '',
  lead_booker_fields: [],
  lead_tag_categories_enabled: false,
  lead_selected_tag_categories: [],
  apply_same_fields_to_party_members: false,
  collect_party_members_info: true,
  include_party_members_in_chat: false,
  party_member_fields: [],
  party_tag_categories_enabled: false,
  party_selected_tag_categories: [],
  waiver_enabled: false,
  waivers: [],
};

export interface PreCheckField {
  id: string;
  name: string;
  type: string;
  required?: boolean;
  multiselect?: boolean;
  category_id?: string;
  [k: string]: unknown;
}

export type PreCheckScope = 'lead' | 'party';

/** The dashboard's standard fields (`TripPreCheckSettings.js`). */
export const STANDARD_FIELDS: ReadonlyArray<{ id: string; name: string; type: string }> = [
  { id: 'first_name', name: 'First name', type: 'string' },
  { id: 'last_name', name: 'Last name', type: 'string' },
  { id: 'email', name: 'Email', type: 'string' },
  { id: 'is_minor', name: 'Minor', type: 'boolean' },
  { id: 'phone', name: 'Phone number', type: 'string' },
  { id: 'custom_note', name: 'Custom note', type: 'string' },
  { id: 'date_of_birth', name: 'Date of birth', type: 'date' },
  { id: 'passport_number', name: 'Passport number', type: 'string' },
  { id: 'address', name: 'Address', type: 'string' },
  { id: 'emergency_contact', name: 'Emergency contact', type: 'string' },
];

/** A custom field's types (the old tab's choice, and long text). */
export const FIELD_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'string', label: 'Text' },
  { value: 'text', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'date', label: 'Date' },
];

export const TYPE_LABEL: Readonly<Record<string, string>> = {
  ...Object.fromEntries(FIELD_TYPES.map((t) => [t.value, t.label])),
  tag_category: 'Tag category',
  category: 'Tag category',
};

/** Always asked of the lead booker, and required. */
export const LEAD_MANDATORY: ReadonlySet<string> = new Set(['first_name', 'email']);

export const SCOPE_PATHS = {
  lead: {
    fields: PRECHECK('lead_booker_fields'),
    enabled: PRECHECK('lead_tag_categories_enabled'),
    selected: PRECHECK('lead_selected_tag_categories'),
  },
  party: {
    fields: PRECHECK('party_member_fields'),
    enabled: PRECHECK('party_tag_categories_enabled'),
    selected: PRECHECK('party_selected_tag_categories'),
  },
} as const;
export const APPLY_SAME_PATH = PRECHECK('apply_same_fields_to_party_members');

export function asFields(v: unknown): PreCheckField[] {
  return Array.isArray(v)
    ? (v as unknown[]).filter((f): f is PreCheckField => typeof f === 'object' && f !== null && typeof (f as { id?: unknown }).id === 'string')
    : [];
}

export function isQuestion(f: PreCheckField): boolean {
  return f.type === 'tag_category' || f.type === 'category';
}

/** A question's category code. */
export function questionCode(f: PreCheckField): string {
  return String(f.category_id ?? f.id.replace(/^tag_category_/, ''));
}

export function splitFields(list: unknown): { regular: PreCheckField[]; questions: PreCheckField[] } {
  const all = asFields(list);
  return { regular: all.filter((f) => !isQuestion(f)), questions: all.filter(isQuestion) };
}

/** "3 fields · 2 questions" (the ledger's and the view's summary of a list). */
export function describeFields(value: unknown): string {
  const { regular, questions } = splitFields(value);
  if (regular.length === 0 && questions.length === 0) return 'no fields';
  const parts = [`${regular.length} ${regular.length === 1 ? 'field' : 'fields'}`];
  if (questions.length > 0) parts.push(`${questions.length} ${questions.length === 1 ? 'question' : 'questions'}`);
  return parts.join(' · ');
}

/** A field the operator defines (neither a standard field nor a question). */
export function isCustomField(f: PreCheckField): boolean {
  return !STANDARD_FIELDS.some((s) => s.id === f.id) && !isQuestion(f);
}

/** What a draft changed in a field list, item by item. */
export interface FieldListDiff {
  added: PreCheckField[];
  removed: PreCheckField[];
  /** [stored label, new label]. */
  renamed: Array<[string, string]>;
  /** The rows (as they are now) whose Required flipped. */
  required: PreCheckField[];
  /** The rows both lists hold are in another order. */
  reordered: boolean;
}

export function fieldListDiff(from: unknown, to: unknown): FieldListDiff {
  const before = asFields(from);
  const after = asFields(to);
  const was = new Map(before.map((f) => [f.id, f]));
  const now = new Map(after.map((f) => [f.id, f]));
  const diff: FieldListDiff = { added: [], removed: [], renamed: [], required: [], reordered: false };
  for (const f of after) {
    const old = was.get(f.id);
    if (!old) diff.added.push(f);
    else {
      if (!isQuestion(f) && old.name !== f.name) diff.renamed.push([old.name, f.name]);
      if ((old.required === true) !== (f.required === true)) diff.required.push(f);
    }
  }
  diff.removed = before.filter((f) => !now.has(f.id));
  const kept = (list: PreCheckField[], other: Map<string, PreCheckField>) => list.filter((f) => other.has(f.id)).map((f) => f.id).join('|');
  diff.reordered = kept(before, now) !== kept(after, was);
  return diff;
}

const quoted = (f: PreCheckField) => (isQuestion(f) ? `question “${f.name}”` : `“${f.name}”`);

/** A field list's change in words, one line per item (the ledger's notes): added, removed,
 *  relabelled, required, moved. */
export function fieldListNotes(from: unknown, to: unknown): string[] {
  const d = fieldListDiff(from, to);
  const was = new Map(asFields(from).map((f) => [f.id, f]));
  const out: string[] = [];
  // Row by row, as the list now reads; then what left it; then the order.
  for (const f of asFields(to)) {
    const old = was.get(f.id);
    if (!old) {
      out.push(`Added ${quoted(f)}${isCustomField(f) ? ` · ${TYPE_LABEL[f.type] ?? f.type}` : ''}${f.required === true ? ' · required' : ''}`);
      continue;
    }
    if (!isQuestion(f) && old.name !== f.name) out.push(`“${old.name}” renamed “${f.name}”`);
    if ((old.required === true) !== (f.required === true)) out.push(`“${f.name}” ${f.required === true ? 'required' : 'optional'}`);
  }
  out.push(...d.removed.map((f) => `Removed ${quoted(f)}`));
  if (d.reordered) out.push('Order changed');
  return out;
}

/** A field list's change in one line when its counts did not move — where "3 fields → 3
 *  fields" would say nothing ("“Email” → “E-mail address”", "2 fields relabelled · order
 *  changed"); null when the counts moved (they say it). */
export function fieldListSummary(from: unknown, to: unknown): string | null {
  if (describeFields(from) !== describeFields(to)) return null;
  const d = fieldListDiff(from, to);
  const parts: string[] = [];
  if (d.renamed.length === 1) parts.push(`“${d.renamed[0]![0]}” → “${d.renamed[0]![1]}”`);
  else if (d.renamed.length > 1) parts.push(`${d.renamed.length} fields relabelled`);
  if (d.required.length === 1) parts.push(`“${d.required[0]!.name}” ${d.required[0]!.required === true ? 'required' : 'optional'}`);
  else if (d.required.length > 1) parts.push(`${d.required.length} fields’ Required changed`);
  if (d.added.length === 1 && d.removed.length === 1) parts.push(`${quoted(d.removed[0]!)} replaced by ${quoted(d.added[0]!)}`);
  else if (d.added.length + d.removed.length > 0) parts.push(`${d.added.length} added · ${d.removed.length} removed`);
  if (d.reordered) parts.push('order changed');
  if (parts.length === 0) return null;
  const line = parts.join(' · ');
  return /^[a-z]/.test(line) ? `${line[0]!.toUpperCase()}${line.slice(1)}` : line;
}

export function questionField(code: string, name: string | null | undefined, existing?: PreCheckField): PreCheckField {
  return {
    ...(existing ?? {}),
    id: existing?.id ?? `tag_category_${code}`,
    name: name || existing?.name || code,
    type: 'tag_category',
    category_id: code,
    required: false,
    multiselect: existing?.multiselect ?? false,
  };
}

/** A scope as it stands: its regular fields, and its questions' switch and categories. */
export interface ScopeState {
  regular: PreCheckField[];
  questions: PreCheckField[];
  enabled: boolean;
  selected: string[];
}

export function scopeState(valueOf: (path: string) => unknown, scope: PreCheckScope): ScopeState {
  const paths = SCOPE_PATHS[scope];
  const { regular, questions } = splitFields(valueOf(paths.fields));
  const storedSelected = Array.isArray(valueOf(paths.selected)) ? (valueOf(paths.selected) as unknown[]).map(String) : [];
  const enabled = valueOf(paths.enabled) === true || questions.length > 0;
  const selected = questions.length > 0 ? questions.map(questionCode) : storedSelected;
  return { regular, questions, enabled, selected };
}

/** The paths of a scope after an edit, as one set of values. */
export function scopeValues(
  scope: PreCheckScope,
  state: ScopeState,
  edit: { regular?: PreCheckField[]; enabled?: boolean; selected?: string[] },
  nameOf: (code: string) => string | null | undefined,
): Record<string, unknown> {
  const paths = SCOPE_PATHS[scope];
  const regular = edit.regular ?? state.regular;
  const enabled = edit.enabled ?? state.enabled;
  const selected = edit.selected ?? state.selected;
  const questions = enabled
    ? selected.map((code) => questionField(code, nameOf(code), state.questions.find((q) => questionCode(q) === code)))
    : [];
  return { [paths.fields]: [...regular, ...questions], [paths.enabled]: enabled, [paths.selected]: selected };
}

/** The party's paths copied from the lead booker's values (`apply_same_fields…` on): the
 *  lead booker's fields as plain fields (none is mandatory for a party member) and the
 *  same questions. */
export function partyFromLead(leadValues: Record<string, unknown>): Record<string, unknown> {
  const lead = SCOPE_PATHS.lead;
  const party = SCOPE_PATHS.party;
  return {
    [party.fields]: asFields(leadValues[lead.fields]).map((f) => ({ ...f })),
    [party.enabled]: leadValues[lead.enabled],
    [party.selected]: Array.isArray(leadValues[lead.selected]) ? [...(leadValues[lead.selected] as string[])] : [],
  };
}

/** Every path one pre-check edit moves: the scope's, and the party's when it follows the
 *  lead booker (`applySame`). */
export function precheckEdit(
  valueOf: (path: string) => unknown,
  scope: PreCheckScope,
  edit: { regular?: PreCheckField[]; enabled?: boolean; selected?: string[] },
  nameOf: (code: string) => string | null | undefined,
): Record<string, unknown> {
  const values = scopeValues(scope, scopeState(valueOf, scope), edit, nameOf);
  if (scope === 'lead' && valueOf(APPLY_SAME_PATH) === true) Object.assign(values, partyFromLead(values));
  return values;
}

/** Turning "same fields for party members" on copies the lead booker's now. */
export function applySameEdit(
  valueOf: (path: string) => unknown,
  on: boolean,
  nameOf: (code: string) => string | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { [APPLY_SAME_PATH]: on };
  if (on) Object.assign(out, partyFromLead(scopeValues('lead', scopeState(valueOf, 'lead'), {}, nameOf)));
  return out;
}

/** A NEW custom field's id is namespaced (`custom_<slug>`), so it can never take a
 *  standard field's or a stored column's name; the backend refuses any other new id.
 *  Custom fields stored under an older, bare id keep it (ids are never re-derived from a
 *  renamed label). */
export const CUSTOM_FIELD_PREFIX = 'custom_';
export const FIELD_ID_MAX_LENGTH = 64;

/** A field id for a new custom field's name ("Dietary needs" → `custom_dietary_needs`):
 *  lowercase `[a-z0-9_]`, accents folded, at most 64 characters. */
export function fieldId(name: string): string {
  const slug =
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'field';
  return `${CUSTOM_FIELD_PREFIX}${slug}`.slice(0, FIELD_ID_MAX_LENGTH).replace(/_+$/, '');
}

/** One of the operator's custom fields (`Pre_Check_In_Field`): shared by every tour that
 *  asks it, so a list adds it by id rather than creating it again. */
export interface CustomFieldDefinition {
  id: string;
  name: string;
  type: string;
}

/** Why a new custom field named `name` cannot join a list holding `existing`: its id is a
 *  standard field's, a field of the list already has it, or one of the operator's custom
 *  fields (`definitions`, offered by "Add a field" instead). `null` when it can. */
export function customFieldClash(
  name: string,
  existing: ReadonlyArray<{ id: string; name: string }>,
  definitions: ReadonlyArray<CustomFieldDefinition> = [],
): string | null {
  if (name.trim() === '') return null;
  const id = fieldId(name);
  const standard = STANDARD_FIELDS.find((f) => f.id === id);
  if (standard) return `Clashes with the standard field “${standard.name}” — use another name`;
  const hit = existing.find((f) => f.id === id);
  if (hit) {
    return hit.name.trim().toLowerCase() === name.trim().toLowerCase() ? 'Already in the list' : `Clashes with “${hit.name}” — use another name`;
  }
  const defined = definitions.find((d) => d.id === id);
  return defined ? `“${defined.name}” already exists — add it from the list` : null;
}

/** What a change of a field list does to the operator's OTHER tours (`Pre_Check_In_Field`
 *  is keyed by the operator and the field's id, and a save overwrites its name and type):
 *  a renamed field is renamed on every tour of the operator; a custom field added under
 *  an id the operator already uses takes this name and type everywhere. The `required`
 *  flag and the order are the tour's own. Copy only — the behaviour is the funnel's. */
export function sharedFieldWarnings(to: unknown, stored: unknown, operatorName: string | null | undefined): string[] {
  const who = operatorName ? `every tour of ${operatorName}` : 'every tour of the operator';
  const before = new Map(splitFields(stored).regular.map((f) => [f.id, f]));
  const out: string[] = [];
  for (const f of splitFields(to).regular) {
    const was = before.get(f.id);
    if (was && (was.name !== f.name || was.type !== f.type)) {
      out.push(`“${f.name}” is shared by ${who} — renaming it changes it on all of them`);
    } else if (!was && !STANDARD_FIELDS.some((s) => s.id === f.id)) {
      out.push(`Custom field “${f.name}” is shared by ${who} — if one of them already has it, its name and type change on all of them`);
    }
  }
  return out;
}
